import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import testimonialService from '../services/testimonial.service';

const TestimonialApproval = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    const approveTestimonial = async () => {
      try {
        const token = searchParams.get('token');
        
        if (!token) {
          setStatus('error');
          setMessage('Invalid approval link. No token found.');
          return;
        }

        const response = await testimonialService.approve(token);
        
        if (response?.data) {
          setTestimonial(response.data);
          setStatus('success');
          setMessage('Thank you! Your testimonial has been approved and will appear on our website shortly.');
        } else {
          setStatus('error');
          setMessage('Failed to approve testimonial. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Testimonial approval error:', error);
        const errorMessage = error?.response?.data?.message || 'An error occurred while approving your testimonial';
        setStatus('error');
        setMessage(errorMessage);
      }
    };

    approveTestimonial();
  }, [searchParams]);

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-b from-compassionScale-50 to-white flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader className="w-16 h-16 text-spiritual mx-auto mb-4 animate-spin" />
                <h1 className="text-2xl font-heading font-bold text-spiritual mb-2">
                  Processing Your Approval
                </h1>
                <p className="text-compassion-foreground">
                  Please wait while we verify and approve your testimonial...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-spiritual mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-bold text-spiritual mb-2">
                  Testimonial Approved!
                </h1>
                <p className="text-compassion-foreground mb-6">
                  {message}
                </p>
                {testimonial && (
                  <div className="bg-compassionScale-50 p-4 rounded-lg mb-6 text-left border border-compassionScale-200">
                    <p className="text-sm font-semibold text-spiritual mb-2">Your Testimonial:</p>
                    <blockquote className="text-gray-700 italic mb-2">
                      "{testimonial.text}"
                    </blockquote>
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-yellow-400">★</span>
                      ))}
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-spiritual hover:bg-spiritual/90 text-white"
                >
                  Return to Home
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                <h1 className="text-2xl font-heading font-bold text-spiritual mb-2">
                  Approval Failed
                </h1>
                <p className="text-red-600 mb-6">
                  {message}
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/')}
                    className="flex-1 bg-spiritual hover:bg-spiritual/90 text-white"
                  >
                    Back to Home
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="flex-1"
                  >
                    Contact Support
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-compassion-foreground text-sm mt-6">
          If you have any questions, please{' '}
          <a href="mailto:healingatmysticprana@gmail.com" className="font-semibold hover:underline">
            contact us
          </a>
        </p>
      </div>
    </div>
  );
};

export default TestimonialApproval;
